import React, { useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { CardRenderContext, ListViewContextAction, RowFlashState } from './types'
import type { SelectionAction } from '../Table/types'
import type { ListViewCTAGroup } from './components/ListViewCard'
import { CardCtaOverlay } from './components/ListViewCard/CardCtaOverlay'
import * as s from './StackView.css'
import { DropdownMenu, MenuItem } from '../DropdownMenu'
import { ScrollOverflowFades } from '../Table/ScrollOverflowFades'
import { useScrollOverflowEdges } from '../Table/useScrollOverflowEdges'
import { useT } from "../../../app/lib/i18n";

export interface StackViewProps<TRow extends { id: string }> {
  rows: TRow[]
  loading?: boolean
  fetching?: boolean
  renderCard: (row: TRow, ctx: CardRenderContext) => ReactNode
  fillContainer?: boolean
  emptyTitle?: string
  emptySubtitle?: string
  emptyAction?: ReactNode
  onRowClick?: (row: TRow) => void
  selectedIds?: Set<string>
  onSelectionChange?: (ids: Set<string>) => void
  enableSelection?: boolean
  /** At most one row selected; no checkboxes — card click selects. */
  singleSelect?: boolean
  selectionActions?: SelectionAction[]
  getRowLabel?: (row: TRow) => string
  getRowCtas?: (row: TRow) => ListViewCTAGroup[] | undefined
  getContextActions?: (row: TRow) => ListViewContextAction[]
  flashingRows?: Map<string, RowFlashState>
  compact?: boolean
}

interface SelectionProps {
  selectedIds?: Set<string>
  onSelectionChange?: (ids: Set<string>) => void
  enableSelection?: boolean
  singleSelect?: boolean
  selectionActions?: SelectionAction[]
  rowLabel?: string
}

interface CardMenuAction {
  id: string
  label: string
  icon?: ReactNode
  danger?: boolean
  disabled?: boolean
  disabledTooltip?: string
  onClick: () => void
}

function RowContextMenu({
  actions, onOpenChange }: { actions: CardMenuAction[]; onOpenChange?: (open: boolean) => void }) {
  const t = useT();
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const handleSetOpen = (v: boolean) => { setOpen(v); onOpenChange?.(v) }
  if (!actions.length) return null
  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={s.stackRowMenuBtn}
        aria-label={t("shared.listView.moreOptions")}
        onClick={e => { e.stopPropagation(); handleSetOpen(!open) }}
      >
        <svg width={14} height={14} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" />
        </svg>
      </button>
      <DropdownMenu
        anchorEl={anchorRef}
        anchorPos="SE"
        openDirection="SW"
        open={open}
        setOpen={handleSetOpen}
        onChange={v => actions.find(a => a.id === v)?.onClick()}
        closeOnSelect
      >
        {actions.map(action => (
          <MenuItem key={action.id} value={action.id} disabled={action.disabled} title={action.disabledTooltip}>
            <span className={action.danger ? s.stackRowMenuItemDanger : undefined} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {action.icon}
              {action.label}
            </span>
          </MenuItem>
        ))}
      </DropdownMenu>
    </>
  )
}

function RowWrapper<TRow extends { id: string }>({
  row,
  renderCard,
  onRowClick,
  selectedIds,
  onSelectionChange,
  enableSelection,
  singleSelect,
  selectionActions,
  rowLabel,
  getRowCtas,
  getContextActions,
  flashingRows,
  compact,
}: {
  row: TRow
  renderCard: (row: TRow, ctx: CardRenderContext) => ReactNode
  onRowClick?: (row: TRow) => void
  getRowCtas?: (row: TRow) => ListViewCTAGroup[] | undefined
  getContextActions?: (row: TRow) => ListViewContextAction[]
  flashingRows?: Map<string, RowFlashState>
  compact?: boolean
} & SelectionProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const activatedByCardRef = useRef(false)
  const isSelected = selectedIds?.has(row.id) ?? false
  const flashInfo = flashingRows?.get(row.id)
  const showCheckbox = Boolean(enableSelection && !singleSelect)
  const singleSelectMode = Boolean(enableSelection && singleSelect)

  const selectThis = () => {
    if (!onSelectionChange) return
    onSelectionChange(new Set([row.id]))
  }

  const activate = () => {
    if (singleSelectMode) selectThis()
    onRowClick?.(row)
  }

  const ctx: CardRenderContext = {
    isSelected,
    onClick: () => {
      activatedByCardRef.current = true
      activate()
    },
    changedFields: flashInfo?.fields,
    updateGen: flashInfo?.gen,
    compact,
  }

  const toggleSelect = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation()
    if (!onSelectionChange || !selectedIds) return
    const next = new Set(selectedIds)
    if (next.has(row.id)) next.delete(row.id)
    else next.add(row.id)
    onSelectionChange(next)
  }

  const ctaGroups = getRowCtas?.(row)

  // Prefer getContextActions (per-row unified); fall back to selectionActions for backward compat
  const menuActions: CardMenuAction[] = getContextActions
    ? getContextActions(row).map(a => ({
        id: a.id,
        label: a.label,
        icon: a.icon,
        danger: a.danger,
        disabled: a.disabled,
        disabledTooltip: a.disabledTooltip,
        onClick: () => a.onClick([row.id]),
      }))
    : (selectionActions ?? []).map((a, i) => ({
        id: String(i),
        label: a.label,
        icon: a.icon,
        danger: a.danger,
        onClick: () => a.onClick([row.id], rowLabel ? [rowLabel] : undefined),
      }))

  const wrapClass = [
    s.stackRowWrap,
    isSelected ? s.stackRowWrapSelected : '',
    menuOpen ? s.stackRowWrapMenuOpen : '',
    singleSelectMode ? s.stackRowWrapSingleSelect : '',
  ].filter(Boolean).join(' ')

  return (
    <div
      className={wrapClass}
      onClick={singleSelectMode ? () => {
        if (activatedByCardRef.current) {
          activatedByCardRef.current = false
          return
        }
        activate()
      } : undefined}
      onKeyDown={singleSelectMode ? (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault()
          activate()
        }
      } : undefined}
      role={singleSelectMode ? 'option' : undefined}
      aria-selected={singleSelectMode ? isSelected : undefined}
      tabIndex={singleSelectMode ? 0 : undefined}
    >
      {showCheckbox && (
        <div
          role="checkbox"
          aria-checked={isSelected}
          aria-label={isSelected ? 'Deselect' : 'Select'}
          tabIndex={0}
          className={`${s.stackRowCheck}${isSelected ? ' is-checked' : ''}`}
          onClick={toggleSelect}
          onKeyDown={e => {
            if (e.key === ' ' || e.key === 'Enter') toggleSelect(e)
          }}
        >
          {isSelected && (
            <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
        </div>
      )}
      {flashInfo && <div key={flashInfo.gen} className={s.updateShimmerOverlay} aria-hidden="true" />}
      <RowContextMenu actions={menuActions} onOpenChange={setMenuOpen} />
      {renderCard(row, ctx)}
      {ctaGroups?.length ? <CardCtaOverlay groups={ctaGroups} layout="stack" /> : null}
    </div>
  )
}

export function StackView<TRow extends { id: string }>({
  rows,
  loading,
  fetching,
  renderCard,
  fillContainer = false,
  emptyTitle = 'No results',
  emptySubtitle,
  emptyAction,
  onRowClick,
  selectedIds,
  onSelectionChange,
  enableSelection,
  singleSelect,
  selectionActions,
  getRowLabel,
  getRowCtas,
  getContextActions,
  flashingRows,
  compact,
}: StackViewProps<TRow>) {
  const wrapClass = fillContainer ? s.stackWrapFill : s.stackWrap
  const scrollRef = useRef<HTMLDivElement>(null)
  const edges = useScrollOverflowEdges(scrollRef, [rows])

  if (loading) {
    return (
      <div className={wrapClass}>
        <div className={s.stackList}>
          {Array.from({ length: 5 }).map((_, i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      </div>
    )
  }

  const content = rows.length === 0 ? (
    <div className={s.stackEmpty}>
      <span>{emptyTitle}</span>
      {emptySubtitle && <span style={{ fontSize: 12, opacity: 0.7 }}>{emptySubtitle}</span>}
      {emptyAction ? <div className={s.stackEmptyAction}>{emptyAction}</div> : null}
    </div>
  ) : (
    <div className={s.stackList}>
      {rows.map(row => (
        <RowWrapper
          key={row.id}
          row={row}
          renderCard={renderCard}
          onRowClick={onRowClick}
          selectedIds={selectedIds}
          onSelectionChange={onSelectionChange}
          enableSelection={enableSelection}
          singleSelect={singleSelect}
          selectionActions={selectionActions}
          rowLabel={getRowLabel?.(row)}
          getRowCtas={getRowCtas}
          getContextActions={getContextActions}
          flashingRows={flashingRows}
          compact={compact}
        />
      ))}
    </div>
  )

  return (
    <div className={wrapClass}>
      {fetching && <div className={s.stackFetchOverlay} aria-hidden="true" />}
      {fillContainer ? (
        <>
          <div ref={scrollRef} className={s.stackScrollWrap}>{content}</div>
          <ScrollOverflowFades edges={edges} />
        </>
      ) : (
        content
      )}
    </div>
  )
}

function SkeletonRow() {
  return (
    <div className={s.stackSkeletonRow} aria-hidden="true">
      <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--color-border)', flexShrink: 0 }} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ height: 12, width: '40%', borderRadius: 4, background: 'var(--color-border)' }} />
        <div style={{ height: 10, width: '60%', borderRadius: 4, background: 'var(--color-border)', opacity: 0.6 }} />
      </div>
    </div>
  )
}
